import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../context/AuthContext';
import { sendMessage, getHistory, getSession, setTokenProvider, captureKnowledge } from '../api/client';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  citations?: Array<{ document: string; section?: string; relevance?: number }>;
  attachmentNames?: string[];
  timestamp: string;
}

interface Session {
  sessionId: string;
  title: string;
  module: string;
  createdAt: string;
  messageCount: number;
}

export function ChatPage() {
  const { user, getToken } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarWidth, setSidebarWidth] = useState(280);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [isListening, setIsListening] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<Array<{name: string; type: string; content: string}>>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [ratedMessages, setRatedMessages] = useState<Record<number, string>>({});
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<any>(null);

  useEffect(() => {
    setTokenProvider(getToken);
    loadSessions();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const [sessionLimit, setSessionLimit] = useState(20);

  async function loadSessions(limit?: number) {
    try {
      const data = await getHistory(limit || sessionLimit);
      setSessions(data.sessions || []);
    } catch (err) {
      console.error('Failed to load sessions:', err);
    }
  }

  async function loadMoreSessions() {
    const newLimit = sessionLimit + 20;
    setSessionLimit(newLimit);
    await loadSessions(newLimit);
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (files) processFiles(Array.from(files));
    e.target.value = ''; // reset so same file can be re-selected
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files) processFiles(Array.from(files));
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(true);
  }

  function handleDragLeave(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
  }

  function autoResizeTextarea() {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto'; // reset to recalculate
    const minHeight = 72; // ~3 rows
    const maxHeight = 192; // ~8 rows
    const scrollHeight = el.scrollHeight;
    el.style.height = `${Math.min(Math.max(scrollHeight, minHeight), maxHeight)}px`;
  }

  async function processFiles(files: File[]) {
    const maxSize = 4.5 * 1024 * 1024; // 4.5MB per file (base64 adds ~33%, must stay under API Gateway 6MB limit)
    
    for (const file of files) {
      if (file.size > maxSize) {
        alert(`"${file.name}" is too large (${(file.size / 1024 / 1024).toFixed(1)}MB).\nMaximum file size is 4.5MB.\nTip: For large PDFs, try compressing them or attaching just the relevant pages.`);
        continue;
      }
      
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        setAttachedFiles(prev => [...prev, {
          name: file.name,
          type: file.type || 'application/octet-stream',
          content: base64,
        }]);
      };
      reader.onerror = () => {
        alert(`Failed to read "${file.name}". Try again.`);
      };
      reader.readAsDataURL(file);
    }
  }

  function removeAttachment(index: number) {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  }

  async function handleSend() {
    if ((!input.trim() && attachedFiles.length === 0) || isLoading) return;

    const currentInput = input;
    const currentAttachments = [...attachedFiles];

    const attachmentNames = currentAttachments.map(f => f.name);
    const userMessage: Message = { 
      role: 'user', 
      content: currentInput || 'Review attached file(s)',
      attachmentNames: attachmentNames.length > 0 ? attachmentNames : undefined,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setAttachedFiles([]);
    setTimeout(autoResizeTextarea, 0);
    setIsLoading(true);

    try {
      const response = await sendMessage(
        currentInput || 'Please review the attached file(s).',
        sessionId,
        currentAttachments.length > 0 ? currentAttachments : undefined
      );
      const assistantMessage: Message = {
        role: 'assistant',
        content: response.response,
        citations: response.citations,
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, assistantMessage]);
      setSessionId(response.sessionId);
    } catch (err: any) {
      let errorMsg = err.message || 'Something went wrong. Please try again.';
      if (currentAttachments.length > 0 && (errorMsg.includes('500') || errorMsg.includes('went wrong'))) {
        errorMsg += '\n\n💡 Tip: If this keeps happening with file attachments, the file may be too large or in an unsupported format. Try a smaller file or paste the text directly.';
      }
      if (errorMsg.includes('413') || errorMsg.includes('too large') || errorMsg.includes('payload')) {
        errorMsg = 'The attached file(s) are too large for the API. Try a smaller file (under 4MB) or send your question without the attachment.';
      }
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: `Error: ${errorMsg}`,
        timestamp: new Date().toISOString(),
      }]);
    } finally {
      setIsLoading(false);
    }
  }

  function handleNewChat() {
    setMessages([]);
    setSessionId(undefined);
    loadSessions();
  }

  async function handleLoadSession(sid: string) {
    try {
      const data = await getSession(sid);
      setMessages(data.messages || []);
      setSessionId(sid);
    } catch (err) {
      console.error('Failed to load session:', err);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  function handleCopy(text: string, index: number) {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    });
  }

  function rateMessage(index: number, rating: string) {
    setRatedMessages(prev => ({...prev, [index]: rating}));
  }

  async function flagMessage(index: number, aiResponse: string) {
    const userMsg = messages[index - 1]?.content || '';
    const reason = prompt('What seems wrong? (optional)');
    if (reason === null) return;

    try {
      const token = await getToken();
      await fetch(`${import.meta.env.VITE_API_URL || 'https://oa2v2yv978.execute-api.us-east-1.amazonaws.com'}/roadmap`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          title: `⚠️ Flagged: "${userMsg.substring(0, 60)}..."`,
          description: reason || 'No details provided',
          category: 'flag',
          type: 'feedback',
          context: `Question: ${userMsg}\n\nAI Response: ${aiResponse.substring(0, 500)}`,
        }),
      });
      setRatedMessages(prev => ({...prev, [index]: 'flagged'}));
    } catch (err) {
      console.error('Failed to flag:', err);
    }
  }

  async function saveAsKnowledge(content: string, index: number) {
    const userMsg = messages[index - 1]?.content || '';
    try {
      await captureKnowledge({
        content,
        context: userMsg,
        category: 'general',
      });
      setRatedMessages(prev => ({...prev, [index]: 'saved'}));
    } catch (err) {
      console.error('Failed to save knowledge:', err);
      alert('Failed to save as knowledge');
    }
  }

  function toggleVoiceInput() {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('Voice input is not supported in this browser. Try Chrome or Edge.');
      return;
    }

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event: any) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      setInput(transcript);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }

  function handleResizeStart(e: React.MouseEvent) {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = sidebarWidth;

    function onMouseMove(e: MouseEvent) {
      const newWidth = Math.max(200, Math.min(400, startWidth + (e.clientX - startX)));
      setSidebarWidth(newWidth);
    }

    function onMouseUp() {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    }

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  return (
    <div className="h-full flex bg-gray-50 overflow-hidden">
      {/* Sidebar — Resizable */}
      {sidebarOpen && (
        <div
          className="bg-gradient-to-b from-[#1a2332] to-[#0f1a2e] text-white flex flex-col overflow-hidden flex-shrink-0"
          style={{ width: `${sidebarWidth}px`, minWidth: '200px', maxWidth: '400px' }}
        >
          <div className="p-3 border-b border-white/10 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-300">Chat History</span>
            <button onClick={() => setSidebarOpen(false)} className="text-gray-400 hover:text-white text-xs">
              &#10005;
            </button>
          </div>
          <div className="p-3">
            <button onClick={handleNewChat} className="w-full py-2 px-3 bg-[#B8996B] text-[#1a2332] rounded font-semibold text-sm hover:bg-opacity-90">
              + New Chat
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-1">
            {groupSessionsByDate(sessions).map((group) => (
              <div key={group.label}>
                <div className="text-xs text-gray-500 font-medium px-3 py-1 mt-2 first:mt-0">{group.label}</div>
                {group.sessions.map((s) => (
                  <button
                    key={s.sessionId}
                    onClick={() => handleLoadSession(s.sessionId)}
                    className={`w-full text-left px-3 py-2 rounded text-sm hover:bg-white/10 truncate ${sessionId === s.sessionId ? 'bg-white/10' : ''}`}
                    title={s.title}
                  >
                    {s.title}
                  </button>
                ))}
              </div>
            ))}
            {sessions.length >= sessionLimit && sessionLimit < 100 && (
              <button
                onClick={loadMoreSessions}
                className="w-full py-2 px-3 text-xs text-gray-400 hover:text-white hover:bg-white/5 rounded mt-2"
              >
                Load more...
              </button>
            )}
          </div>
        </div>
      )}

      {/* Resize Handle */}
      {sidebarOpen && (
        <div
          className="w-1 cursor-col-resize bg-transparent hover:bg-[#4AABDE]/50 transition-colors flex-shrink-0"
          onMouseDown={handleResizeStart}
        />
      )}

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="h-12 border-b bg-white flex items-center px-4">
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="mr-3 text-gray-500 hover:text-gray-700">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <h3 className="font-medium text-gray-700 text-sm">
            {sessionId ? 'Conversation' : 'New Conversation'}
          </h3>
        </div>

        {/* Messages */}
        <div 
          className={`flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-4 ${isDragging ? 'bg-blue/5 border-2 border-dashed border-blue' : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          {messages.length === 0 && (
            <div className="text-center text-gray-400 mt-20">
              <h3 className="text-xl font-medium mb-2">How can I help?</h3>
              <p className="text-sm">Ask me about warranties, operations, CAD, sales, or anything in the knowledge base.</p>
            </div>
          )}
          {messages.map((msg, i) => (
            <div key={i} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
              {/* File attachment chips — shown above the bubble for user messages */}
              {msg.role === 'user' && msg.attachmentNames && msg.attachmentNames.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-1 max-w-[75%] justify-end">
                  {msg.attachmentNames.map((name, j) => (
                    <span key={j} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 border rounded text-xs text-gray-600">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                      {name}
                    </span>
                  ))}
                </div>
              )}
              {/* Message bubble */}
              <div className={`max-w-[75%] rounded-lg px-4 py-3 overflow-hidden relative group ${
                msg.role === 'user' ? 'bg-blue text-white' : 'bg-white border shadow-sm'
              }`}>
                {msg.role === 'assistant' ? (
                  <>
                    {/* Copy button */}
                    <button
                      onClick={() => handleCopy(msg.content, i)}
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded bg-gray-100 hover:bg-gray-200 text-gray-500"
                      title="Copy to clipboard"
                    >
                      {copiedIndex === i ? (
                        <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      )}
                    </button>
                    <div className="prose prose-sm max-w-none">
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                      {msg.citations && msg.citations.length > 0 && (
                        <div className="mt-3 pt-3 border-t text-xs text-gray-500">
                          <strong>Sources:</strong>
                          {msg.citations.map((c, j) => (
                            <span key={j} className="ml-1">{c.document}{j < (msg.citations || []).length - 1 ? ',' : ''}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Rating buttons */}
                    <div className="mt-2 pt-2 border-t flex items-center gap-2">
                      {ratedMessages[i] === 'flagged' ? (
                        <span className="text-xs text-orange-500">⚠️ Flagged for review</span>
                      ) : ratedMessages[i] === 'saved' ? (
                        <span className="text-xs text-[#4AABDE]">💡 Submitted for knowledge review</span>
                      ) : ratedMessages[i] ? (
                        <span className="text-xs text-gray-400">Thanks for the feedback</span>
                      ) : (
                        <>
                          <div className="relative inline-block [&:hover>div]:opacity-100">
                            <button onClick={() => rateMessage(i, 'helpful')} className="text-gray-400 hover:text-green-500 text-sm">👍</button>
                            <div className="absolute bottom-full left-0 mb-2 w-48 p-2 bg-[#1a2332] text-white text-xs rounded-lg shadow-lg opacity-0 transition-opacity pointer-events-none z-50">
                              <p className="text-gray-300">This answer was accurate, clear, and useful for what I needed.</p>
                              <div className="absolute bottom-0 left-4 translate-y-1/2 rotate-45 w-2 h-2 bg-[#1a2332]"></div>
                            </div>
                          </div>
                          <div className="relative inline-block [&:hover>div]:opacity-100">
                            <button onClick={() => rateMessage(i, 'unhelpful')} className="text-gray-400 hover:text-red-500 text-sm">👎</button>
                            <div className="absolute bottom-full left-0 mb-2 w-48 p-2 bg-[#1a2332] text-white text-xs rounded-lg shadow-lg opacity-0 transition-opacity pointer-events-none z-50">
                              <p className="text-gray-300">This answer was inaccurate, confusing, or didn't address my question.</p>
                              <div className="absolute bottom-0 left-4 translate-y-1/2 rotate-45 w-2 h-2 bg-[#1a2332]"></div>
                            </div>
                          </div>
                          <div className="relative inline-block [&:hover>div]:opacity-100">
                            <button onClick={() => flagMessage(i, msg.content)} className="text-gray-400 hover:text-orange-500 text-xs flex items-center gap-1">
                              ⚠️ <span>Flag</span>
                            </button>
                            <div className="absolute bottom-full left-0 mb-2 w-56 p-2 bg-[#1a2332] text-white text-xs rounded-lg shadow-lg opacity-0 transition-opacity pointer-events-none z-50">
                              <p className="text-gray-300">Flag this response for owner review. Use when the AI gave dangerous advice, wrong information, or something that needs to be corrected in the knowledge base.</p>
                              <div className="absolute bottom-0 left-4 translate-y-1/2 rotate-45 w-2 h-2 bg-[#1a2332]"></div>
                            </div>
                          </div>
                          <div className="relative inline-block [&:hover>div]:opacity-100 ml-auto">
                            <button onClick={() => saveAsKnowledge(msg.content, i)} className="text-gray-400 hover:text-[#4AABDE] text-xs flex items-center gap-1">
                              💡 <span>Save as Knowledge</span>
                            </button>
                            <div className="absolute bottom-full right-0 mb-2 w-64 p-3 bg-[#1a2332] text-white text-xs rounded-lg shadow-lg opacity-0 transition-opacity pointer-events-none z-50">
                              <p className="font-medium mb-1">Save as Company Knowledge</p>
                              <p className="text-gray-300 leading-relaxed">Saves this answer as a permanent, searchable company resource. Use it when AI gives a great answer that others should be able to find later — especially if you've corrected or added to it. Submitted entries go through manager approval before publishing.</p>
                              <div className="absolute bottom-0 right-4 translate-y-1/2 rotate-45 w-2 h-2 bg-[#1a2332]"></div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </>
                ) : (
                  <p className="text-sm">{msg.content}</p>
                )}
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-white border shadow-sm rounded-lg px-4 py-3">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="border-t bg-white p-4">
          {/* Attached files preview */}
          {attachedFiles.length > 0 && (
            <div className="max-w-4xl mx-auto mb-2 p-2 bg-blue/5 rounded-lg border border-blue/20">
              <p className="text-xs text-gray-500 mb-1">📎 Attached files:</p>
              <div className="flex flex-wrap gap-2">
              {attachedFiles.map((f, i) => (
                <div key={i} className="flex items-center gap-1 bg-gray-100 rounded px-2 py-1 text-xs">
                  <span className="text-gray-600 max-w-[150px] truncate">{f.name}</span>
                  <button onClick={() => removeAttachment(i)} className="text-gray-400 hover:text-red-500">×</button>
                </div>
              ))}
              </div>
            </div>
          )}
          <div className="max-w-4xl mx-auto flex gap-2">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf,.txt,.html,.eml,.msg,.docx"
              onChange={handleFileSelect}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="px-3 py-2 rounded-lg border bg-white text-gray-500 hover:text-gray-700 hover:border-gray-400"
              title="Attach file"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
            </button>
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => { setInput(e.target.value); setTimeout(autoResizeTextarea, 0); }}
              onKeyDown={handleKeyDown}
              placeholder={isListening ? '🎤 Listening...' : 'Ask a question...'}
              className={`flex-1 px-4 py-2 border rounded-lg resize-none focus:ring-2 focus:ring-gold focus:border-gold ${isListening ? 'border-red-400 bg-red-50' : ''}`}
              rows={3}
              style={{ minHeight: '72px', maxHeight: '192px', overflow: 'auto' }}
            />
            <button
              onClick={toggleVoiceInput}
              className={`px-3 py-2 rounded-lg border transition-colors ${
                isListening 
                  ? 'bg-red-500 text-white border-red-500 animate-pulse' 
                  : 'bg-white text-gray-500 hover:text-gray-700 hover:border-gray-400'
              }`}
              title={isListening ? 'Stop listening' : 'Voice input'}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              </svg>
            </button>
            <button
              onClick={handleSend}
              disabled={isLoading || (!input.trim() && attachedFiles.length === 0)}
              className="px-4 py-2 bg-blue text-white rounded-lg hover:bg-opacity-90 disabled:opacity-50"
            >
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface SessionGroup {
  label: string;
  sessions: Array<{ sessionId: string; title: string; module: string; createdAt: string; messageCount: number }>;
}

function groupSessionsByDate(sessions: Array<{ sessionId: string; title: string; module: string; createdAt: string; messageCount: number }>): SessionGroup[] {
  const groups = new Map<string, SessionGroup & { sortDate: string }>();
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const todayStr = today.toISOString().split('T')[0];
  const yesterdayStr = yesterday.toISOString().split('T')[0];

  for (const session of sessions) {
    const dateStr = session.createdAt?.split('T')[0] || todayStr;
    let label: string;

    if (dateStr === todayStr) {
      label = 'Today';
    } else if (dateStr === yesterdayStr) {
      label = 'Yesterday';
    } else {
      const [year, month, day] = dateStr.split('-');
      label = `${parseInt(month)}/${parseInt(day)}/${year}`;
    }

    if (!groups.has(dateStr)) {
      groups.set(dateStr, { label, sessions: [], sortDate: dateStr });
    }
    groups.get(dateStr)!.sessions.push(session);
  }

  // Sort groups newest first
  return Array.from(groups.values())
    .sort((a, b) => b.sortDate.localeCompare(a.sortDate))
    .map(({ label, sessions }) => ({ label, sessions }));
}
